import { motion, type Easing } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";

const easeOut: Easing = [0.16, 1, 0.3, 1];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: easeOut,
    },
  },
};

export const AboutSection = () => {
  const { t } = useLanguage();

  return (
    <section id="o-nas" className="relative bg-transparent pt-16 pb-12 lg:pt-28 lg:pb-20 scroll-mt-16 lg:scroll-mt-20" style={{ zIndex: 10 }}>
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {/* Headline */}
          <motion.h2
            variants={itemVariants}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-20 lg:mb-20"
            style={{
              color: '#ffffff',
              textShadow: '0 2px 12px rgba(0, 0, 0, 0.5)'
            }}
          >
            {t.about.title}
          </motion.h2>

          {/* Two-column content blocks */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-stretch mt-12 lg:mt-16">
            {/* Block 1: Web/Design */}
            <motion.div
              variants={itemVariants}
              className="relative group"
            >
              <div className="absolute -left-4 top-0 w-px h-full bg-gradient-to-b from-foreground/30 via-foreground/10 to-transparent hidden lg:block" />

              <div className="absolute inset-0 -inset-x-8 -inset-y-8 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none blur-3xl">
                <div
                  className="absolute inset-0"
                  style={{
                    background: 'radial-gradient(ellipse at bottom right, rgba(6, 182, 212, 0.4) 0%, rgba(6, 182, 212, 0.2) 30%, transparent 65%)',
                  }}
                />
              </div>

              <div className="lg:pl-8 relative z-10">
                <span
                  className="text-base lg:text-lg font-medium tracking-[0.2em] uppercase mb-4 block transition-colors duration-500"
                  style={{
                    color: 'rgba(255,255,255,0.85)',
                    fontWeight: 500,
                    textShadow: '0 1px 6px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  <span className="group-hover:text-white group-hover:opacity-100 transition-all duration-500">
                    {t.about.blockLabels?.[0] || "Web & Design"}
                  </span>
                </span>
                <p
                  className="text-base leading-relaxed transition-colors duration-300"
                  style={{
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontWeight: 400,
                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.6)',
                    lineHeight: '1.6'
                  }}
                >
                  <span className="group-hover:text-white transition-colors duration-300">
                    {t.about.description[0]}
                  </span>
                </p>
              </div>
            </motion.div>

            {/* Block 2: AI/Efficiency */}
            <motion.div
              variants={itemVariants}
              className="relative group"
            >
              <div className="absolute -left-4 top-0 w-px h-full bg-gradient-to-b from-foreground/30 via-foreground/10 to-transparent hidden lg:block" />

              <div className="absolute inset-0 -inset-x-8 -inset-y-8 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none blur-3xl">
                <div
                  className="absolute inset-0"
                  style={{
                    background: 'radial-gradient(ellipse at bottom right, rgba(6, 182, 212, 0.4) 0%, rgba(6, 182, 212, 0.2) 30%, transparent 65%)',
                  }}
                />
              </div>

              <div className="lg:pl-8 relative z-10">
                <span
                  className="text-base lg:text-lg font-medium tracking-[0.2em] uppercase mb-4 block transition-colors duration-500"
                  style={{
                    color: 'rgba(255,255,255,0.85)',
                    fontWeight: 500,
                    textShadow: '0 1px 6px rgba(0, 0, 0, 0.5)'
                  }}
                >
                  <span className="group-hover:text-white group-hover:opacity-100 transition-all duration-500">
                    {t.about.blockLabels?.[1] || "AI & Efficiency"}
                  </span>
                </span>
                <p
                  className="text-base leading-relaxed transition-colors duration-300"
                  style={{
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontWeight: 400,
                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.6)',
                    lineHeight: '1.6'
                  }}
                >
                  <span className="group-hover:text-white transition-colors duration-300">
                    {t.about.description[1]}
                  </span>
                </p>
              </div>
            </motion.div>
          </div>

          {/* Divider */}
          <motion.div
            variants={itemVariants}
            className="mt-12 lg:mt-16 mb-12 lg:mb-16"
          >
            <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          </motion.div>

          {/* Block 3: Proč si vybrat AiLum Solutions - Full width */}
          <motion.div
            variants={itemVariants}
            className="relative group mt-12 lg:mt-16 max-w-3xl"
          >
            <div className="absolute -left-4 top-0 w-px h-full bg-gradient-to-b from-foreground/30 via-foreground/10 to-transparent hidden lg:block" />

            <div className="absolute inset-0 -inset-x-8 -inset-y-8 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none blur-3xl">
              <div
                className="absolute inset-0"
                style={{
                  background: 'radial-gradient(ellipse at bottom center, rgba(6, 182, 212, 0.4) 0%, rgba(6, 182, 212, 0.2) 30%, transparent 65%)',
                }}
              />
            </div>

            <div className="lg:pl-8 relative z-10">
              <span
                className="text-base lg:text-lg font-medium tracking-[0.2em] uppercase mb-4 block transition-colors duration-500"
                style={{
                  color: 'rgba(255,255,255,0.85)',
                  fontWeight: 500,
                  textShadow: '0 1px 6px rgba(0, 0, 0, 0.5)'
                }}
              >
                <span className="group-hover:text-white group-hover:opacity-100 transition-all duration-500">
                  {t.about.blockLabels?.[2] || "Proč si vybrat LMD Solutions"}
                </span>
              </span>
              <div
                className="text-base leading-relaxed transition-colors duration-300 space-y-4"
                style={{
                  color: 'rgba(255, 255, 255, 0.85)',
                  fontWeight: 400,
                  textShadow: '0 2px 10px rgba(0, 0, 0, 0.6)',
                  lineHeight: '1.6'
                }}
              >
                {t.about.description[2].split('\n\n').map((paragraph: string, index: number) => (
                  <p key={index} className="group-hover:text-white transition-colors duration-300">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </motion.div>

        </motion.div>
      </div>
    </section>
  );
};
